/**
 * VivaDecor – cart consent (snippets/vd-cart-consent.liquid).
 *
 * - "I agree to the Terms of Service and Privacy Policy" is REQUIRED: checkout and the express
 *   checkout buttons stay blocked until it is ticked.
 * - "I would like to receive emails…" is OPTIONAL. Both choices are saved immediately as cart
 *   attributes ("Terms accepted", "Newsletter opt-in"), so they are on the order. A Shopify Flow
 *   workflow subscribes the customer to email marketing when an order has "Newsletter opt-in" = "Yes".
 * - Checkout is never delayed: the attributes are already saved when the boxes are ticked.
 * - The state survives cart re-renders via sessionStorage.
 */

const VDC_KEY = 'vd-cart-consent';

/** @returns {{terms?: boolean, news?: boolean}} */
function vdcLoad() {
  try {
    return JSON.parse(sessionStorage.getItem(VDC_KEY) || '{}') || {};
  } catch (e) {
    return {};
  }
}

/** @param {{terms?: boolean, news?: boolean}} state */
function vdcSave(state) {
  try {
    sessionStorage.setItem(VDC_KEY, JSON.stringify(state));
  } catch (e) {
    /* storage unavailable */
  }
}

/** @param {Element} root */
function vdcParts(root) {
  return {
    terms: /** @type {HTMLInputElement | null} */ (root.querySelector('[data-vd-consent-terms]')),
    termsErr: /** @type {HTMLElement | null} */ (root.querySelector('[data-vd-consent-terms-err]')),
    news: /** @type {HTMLInputElement | null} */ (root.querySelector('[data-vd-consent-news]')),
    newsOk: /** @type {HTMLElement | null} */ (root.querySelector('[data-vd-consent-news-ok]')),
  };
}

/** Applies the stored state to every consent block on the page. */
function vdcSyncAll() {
  const state = vdcLoad();
  document.querySelectorAll('[data-vd-consent]').forEach((root) => {
    const p = vdcParts(root);
    if (p.terms && p.terms.checked !== !!state.terms) p.terms.checked = !!state.terms;
    if (p.news && p.news.checked !== !!state.news) p.news.checked = !!state.news;
    if (p.newsOk) p.newsOk.hidden = !(p.news && p.news.checked);
    if (p.terms && p.terms.checked && p.termsErr) p.termsErr.hidden = true;
  });
}

/** Saves both choices on the cart (→ order "Additional details"). */
let vdcPending = Promise.resolve();
function vdcSaveToCart() {
  const state = vdcLoad();
  const root = /** @type {HTMLElement | null} */ (document.querySelector('[data-vd-consent]'));
  const updateUrl = (root && root.dataset.cartUpdateUrl) || '/cart/update';
  vdcPending = vdcPending
    .catch(() => {})
    .then(() =>
      fetch(`${updateUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Terms accepted': state.terms ? `Yes (${new Date().toISOString()})` : '',
            'Newsletter opt-in': state.news ? 'Yes' : 'No',
          },
        }),
        keepalive: true,
      })
    )
    .catch(() => {});
  return vdcPending;
}

/** @param {Element} root */
function vdcShowTermsError(root) {
  const p = vdcParts(root);
  if (p.termsErr) p.termsErr.hidden = false;
  if (p.terms) {
    p.terms.focus({ preventScroll: true });
    p.terms.closest('.vd-consent__row')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
}

/** @param {Element} el */
function vdcRootFor(el) {
  const scope = el.closest('.cart-summary__inner, .cart-summary, .cart-drawer__summary, cart-items-component');
  return (scope && scope.querySelector('[data-vd-consent]')) || document.querySelector('[data-vd-consent]');
}

/** @param {MouseEvent} event */
function vdcOnClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const gate = target.closest(
    '.additional-checkout-buttons, #checkout, .cart__checkout-button, button[name="checkout"], input[name="checkout"]'
  );
  if (!gate) return;

  const root = vdcRootFor(gate);
  if (!root) return; // no consent block rendered → never block checkout
  const p = vdcParts(root);

  if (p.terms && !p.terms.checked) {
    event.preventDefault();
    event.stopImmediatePropagation();
    vdcShowTermsError(root);
  }
  // Terms accepted → checkout continues immediately (attributes were saved when the boxes were ticked).
}

/** @param {Event} event */
function vdcOnChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.closest('[data-vd-consent]')) return;

  const state = vdcLoad();
  if (target.matches('[data-vd-consent-terms]')) state.terms = target.checked;
  else if (target.matches('[data-vd-consent-news]')) state.news = target.checked;
  else return;

  vdcSave(state);
  vdcSyncAll();
  vdcSaveToCart();
}

// @ts-ignore
if (!window.__vdCartConsent) {
  // @ts-ignore
  window.__vdCartConsent = true;

  document.addEventListener('click', vdcOnClick, true);
  document.addEventListener('change', vdcOnChange);

  // Keep the state after the theme re-renders the cart (quantity change, remove, add-ons…)
  let vdcQueued = false;
  new MutationObserver(() => {
    if (vdcQueued) return;
    vdcQueued = true;
    requestAnimationFrame(() => {
      vdcQueued = false;
      vdcSyncAll();
    });
  }).observe(document.body, { childList: true, subtree: true });

  vdcSyncAll();

  // Keep the cart in sync with choices restored from this session
  const restored = vdcLoad();
  if ((restored.terms || restored.news) && document.querySelector('[data-vd-consent]')) vdcSaveToCart();
}
